<template>
  <editor-menu-bar
    v-slot="{commands, isActive, getMarkAttrs, focused}"
    :editor="editor"
  >
    <div class="relative px-2 py-1 border bg-white">
      <div class="flex flex-wrap">
        <lm-dropdown
          class="HeadingDrop flex items-center mr-3 cursor-pointer hover:bg-gray-100"
          icon-outline
          :items="viewOptions"
          :selected-item="getSelectedView(isActive)"
          auto-close
          @select="onSelectView($event, commands)"
        >
          <div
            v-tooltip="'Text styles'"
            class="flex justify-between items-center pr-1 border-r"
          >
            <span class="w-16 text-gray-900">{{
              getSelectedView(isActive).label
            }}</span>
            <lm-icon icon="arrowDropDown" size="24" />
          </div>
        </lm-dropdown>
        <lm-icon-button
          v-for="(format, index) in formats"
          :key="index"
          svg
          :icon="format[0]"
          :highlight="format[2] && isActive[format[1]]()"
          :class="[
            {
              'mr-1': format[3] === 1,
              'mr-2': format[3] === 2,
              'mr-3': format[3] === 3,
              'mr-4': format[3] === 4
            }
          ]"
          :disabled="format[0] === Formats.LINK && !isSelection"
          :tooltip="
            format[0] === Formats.LINK && !isSelection
              ? 'Select text to add a link'
              : format[4]
          "
          @click="exec(commands, getMarkAttrs, format[1])"
        />
        <lm-icon-button
          icon="helpOutline"
          svg
          class="ml-auto"
          @click="openHelp"
        />
      </div>
      <link-box
        v-if="linkBoxIsActive"
        :url="link"
        :text="linkText"
        :extension="currentExtension"
        @close="close(commands)"
        @clear="setLink(commands, null)"
        @submit="setLink(commands, $event)"
      />
    </div>
  </editor-menu-bar>
</template>

<script>
import LinkBox from '@/components/form/editor/LinkBox'
import LmDropdown from '@/components/ui/LmDropdown'
import LmIcon from '@/components/ui/LmIcon'
import LmIconButton from '@/components/ui/LmIconButton'
import {Modal, Formats} from '@/lib/definitions'
import {EditorMenuBar} from 'tiptap'

export default {
  name: 'LmEditorMenuBar',
  components: {
    LinkBox,
    LmIcon,
    LmDropdown,
    LmIconButton,
    EditorMenuBar
  },
  props: {
    editor: {
      required: true,
      type: Object
    }
  },
  data() {
    return {
      // [ icon, command, isActive?, space, tooltip ]
      formats: [
        ['bold', 'bold', true, 0, 'Bold (Cmd/Ctrl+B)'],
        ['italic', 'italic', true, 0, 'Italic (Cmd/Ctrl+I)'],
        ['strikethrough', 'strike', true, 4, 'Strikethrough (Cmd/Ctrl+D)'],
        ['listBulleted', 'bullet_list', true, 1, 'Bullet list (Ctrl+Shift+8)'],
        [
          'listNumbered',
          'ordered_list',
          true,
          3,
          'Numbered list (Ctrl+Shift+9)'
        ],
        ['quote', 'blockquote', true, 1, 'Quote (Cmd/Ctrl+>)'],
        ['code', 'code', true, 1, 'Code (Cmd/Ctrl+`)'],
        ['codeblock', 'code_block', true, 4, `Code block (Ctrl+Shift+\\)`],
        ['link', Formats.LINK, true, 1, 'Link (Cmd/Ctrl+K)'],
        ['photo', Formats.IMAGE, false, 1, 'Image'],
        ['movie', Formats.IFRAME, false, 4, 'Media'],
        ['undo', 'undo', false, 1, 'Undo (Cmd/Ctrl+Z)'],
        ['redo', 'redo', false, 0, 'Redo (Cmd/Ctrl+Shift+Z)']
      ],
      viewOptions: [
        {option: 'normal', label: 'Normal'},
        {option: 'h1', label: 'Heading 1'},
        {option: 'h2', label: 'Heading 2'}
      ],
      Formats,
      selectedView: null,
      link: null,
      linkBoxIsActive: false,
      currentExtension: null,
      linkText: null
    }
  },
  computed: {
    isSelection() {
      return !!this.getSelectedText()
    }
  },
  created() {
    this.selectedView = this.viewOptions[0]
  },
  methods: {
    exec(commands, getMarkAttrs, extension) {
      if (
        extension === Formats.LINK ||
        extension === Formats.IMAGE ||
        extension === Formats.IFRAME
      ) {
        if (extension === Formats.LINK) this.link = getMarkAttrs(extension).href
        this.currentExtension = extension
        this.linkText = this.getSelectedText()
        this.showLinkBox()
      } else {
        commands[extension]()
      }
    },
    getSelectedView(isActive) {
      let p = this.viewOptions[0]
      if (isActive.heading({level: 1})) p = this.viewOptions[1]
      else if (isActive.heading({level: 2})) p = this.viewOptions[2]
      return p
    },
    getSelectedText() {
      const {selection, state} = this.editor
      const {from, to} = selection
      return state.doc.textBetween(from, to, ' ')
    },
    showLinkBox() {
      this.linkBoxIsActive = true
    },
    hideLinkBox() {
      this.link = null
      this.linkText = null
      this.linkBoxIsActive = false
      this.$emit('preventHide')
      this.editor.focus()
    },
    close(commands) {
      // Hack to prevent losing focus when cancel image or video
      if (this.currentExtension !== Formats.LINK) {
        commands[Formats.LINK]({href: null})
      }
      this.hideLinkBox()
    },
    setLink(commands, url) {
      const key = this.currentExtension === Formats.LINK ? 'href' : 'src'
      commands[this.currentExtension]({[key]: url})
      this.hideLinkBox()
    },
    onClickLink(href, text) {
      this.link = href
      this.currentExtension = Formats.LINK
      this.linkText = text
      this.showLinkBox()
    },
    onSelectView(item, commands) {
      if (item.option === 'normal') commands.paragraph()
      else if (item.option === 'h1') commands.heading({level: 1})
      else if (item.option === 'h2') commands.heading({level: 2})
      this.selectedView = item
    },
    openHelp() {
      this.$router.pushQuery({submodal: Modal.EDITOR_HELP})
    }
  }
}
</script>

<style>
.HeadingDrop .Dropdown-trigger {
  font-size: 14px;
}
</style>
